import { Constants } from 'src/app/shared/constants'
import DEBOUNCED_TIME = Constants.DEBOUNCED_TIME
import { Component, Input, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import {
  CondOperator,
  QuerySortOperator,
  RequestQueryBuilder
} from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { combineLatest, Observable, of, Subject } from 'rxjs'
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  take,
  tap
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Activity } from '../../activity/activity'
import { activitySelectors } from '../../activity/activity.selectors'

import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { Operation, OperationType } from '../../operation/operation'
import moment from 'moment-mini'
import { productSelectors } from '../../product/product.selectors'
import { organizationSelectors } from '../../organization/organization.selectors'

import { operationActions } from '../../operation/operation.actions'
import { Moment } from 'moment-mini'

import { saleActions } from '../../sale/sale.actions'
import { saleItemActions } from '../../sale/sale-item/sale-item.actions'
import { ApproveBookingModalComponent } from '../../dashboard/approve-booking-modal/approve-booking-modal.component'
import { OperationShowModalComponent } from '../../operation/show-modal/show.component'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { operationSelectors } from '../../operation/operation.selectors'
import { Sale } from '../sale'
import { saleSelectors } from '../sale.selectors'
import { bookingSelectors } from '../../booking/booking.selectors'
import { participationSelectors } from '../../booking/participation/participation.selectors'
import { bookingActions } from '../../booking/booking.actions'
import { documentActions } from '../../document/document.actions'

@UntilDestroy()
@Component({
  selector: 'operation-tab',
  templateUrl: './operation-tab.component.html',
  styleUrls: ['./operation-tab.component.scss']
})
export class OperationTabComponent implements OnInit {
  @Input() sale: Sale
  public activities: Activity[] = []
  public limit: number = 25
  public total: number = 0
  public page: number = 1
  public pageCount: number = 0
  public bsModalRef: BsModalRef
  private startDate: Moment
  public removeSearchTerm$: Subject<boolean> = new Subject<boolean>()
  private endDate: Moment
  public searchTerm$ = new Subject<string>()
  public searchTerm
  public field: string = 'createdAt'
  public order = 'DESC'
  public isLoading$: Observable<boolean>
  public navigationForm: UntypedFormGroup
  public operations$: Subject<Operation[]> = new Subject<Operation[]>()
  constructor(
    private modalService: BsModalService,
    private store: Store<AppState>,
    private actions$: Actions,
    private fb: UntypedFormBuilder,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.isLoading$ = this.store.select(operationSelectors.selectIsLoading)
    if (!this.sale) {
      this.store
        .select(saleSelectors.selectIdRouteParam)
        .pipe(filter((saleId) => Boolean(saleId)))
        .pipe(take(1))
        .subscribe((saleId) => {
          this.store
            .select(saleSelectors.selectById, saleId)
            .pipe(take(1))
            .subscribe((saleItem) => (this.sale = saleItem))
        })
    }
    this.dispatchFetchOperations()
    this.searchTerm$
      .pipe(
        distinctUntilChanged(),
        debounceTime(DEBOUNCED_TIME),
        untilDestroyed(this)
      )
      .subscribe((term) => {
        this.searchTerm = term
        this.page = 1
        this.total = 0

        this.dispatchFetchOperationsWitTerm(term)
      })

    this.actions$
      .pipe(
        untilDestroyed(this),
        ofType(operationActions.upsertMany),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (!action) {
          return
        }
        const saleIds: any = [
          ...new Set(action.entities.map((item) => item.saleId).filter(Boolean))
        ]
        const BookingIds: any = [
          ...new Set(
            action.entities.map((item) => item.bookingId).filter(Boolean)
          )
        ]
        if (saleIds && saleIds.length > 0) {
          this.store.dispatch(
            saleActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  id: {
                    [CondOperator.IN]: saleIds
                  }
                },
                join: [{ field: 'creditSales' }, { field: 'customer' }]
              })
            })
          )
        }
        if (BookingIds && BookingIds.length > 0) {
          this.store.dispatch(
            bookingActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  id: {
                    [CondOperator.IN]: BookingIds
                  }
                },
                join: [
                  { field: 'participations' },
                  { field: 'participations.member' }
                ]
              })
            })
          )
        }
        const actionWithMeta = action as {
          total?: number
          pageCount?: number
        }
        this.total = actionWithMeta.total
        this.pageCount = actionWithMeta.pageCount
        this.mappedOperationFetch(action?.entities)
      })
  }
  public onClickRow(activity: Activity): void {}

  public onSorted(fieldSort) {
    this.order = this.order == 'ASC' ? 'DESC' : 'ASC'
    this.field = fieldSort
    this.page = 1

    if (this.searchTerm) {
      this.dispatchFetchOperationsWitTerm(this.searchTerm)
    } else {
      this.dispatchFetchOperations()
    }
  }

  public onClickSelectedType(type: string) {
    this.navigationForm.controls.selectedType.patchValue(type)
  }
  public onClickPendingOperation(operation: Operation) {
    // if (operation.type === OperationType.OVERBOOKING_APPROVE_MANUAL) {
    //   this.router.navigate([`/bookings/${operation.bookingId}/participation`])
    //   return
    // }
    if (
      operation.type === OperationType.RESOLVE_UNDECIDED_SALE &&
      operation.saleId
    ) {
      this.router.navigate(['/sales', operation.saleId])
      return
    }

    if (operation.type != 'approve-participation') {
      if (operation.saleId) {
        this.store.dispatch(
          saleActions.fetchOneById({
            id: operation.saleId,
            params: RequestQueryBuilder.create({
              join: [
                { field: 'creditSales' },
                { field: 'organization' },
                { field: 'saleItems' },
                { field: 'saleItems.booking' },
                { field: 'saleItems.booking.product' },
                { field: 'saleItems.booking.linkedProduct' },
                { field: 'saleItems.product' },
                { field: 'saleItems.product.translations' },
                { field: 'saleItems.participations' },
                { field: 'payments' }
              ]
            })
          })
        )
      }

      this.bsModalRef = this.modalService.show(OperationShowModalComponent, {
        initialState: {
          operationId: operation.id
        },
        class: 'modal-lg modal-dialog-scrollable'
      })

      return
    }

    if (operation?.bookingId) {
      this.store.dispatch(
        bookingActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: { id: operation?.bookingId },
            join: [
              { field: 'product' },
              { field: 'product.translations' },
              { field: 'product.location' },
              { field: 'participations' },
              { field: 'participations.member' },
              { field: 'participations.organization' },
              { field: 'linkedProduct' },
              //{ field: 'linkedProduct.translations' },
              { field: 'linkedProduct.location' }
            ]
          })
        })
      )
      this.store.dispatch(
        saleItemActions.fetchMany({
          params: RequestQueryBuilder.create({
            join: [
              { field: 'sale' },
              { field: 'product' },
              { field: 'product.translations' },
              { field: 'subscription' },
              { field: 'subscription.translations' },
              { field: 'course' },
              { field: 'course.translations' },
              { field: 'sale.organization' },
              { field: 'sale.customer' },
              { field: 'sale.saleItems' }
            ],
            search: { bookingId: operation?.bookingId }
          })
        })
      )
    } else if (operation.participationId) {
      this.store
        .select(participationSelectors.selectById, operation.participationId)
        .pipe(
          take(1),
          tap((participation) => {
            this.store.dispatch(
              bookingActions.fetchMany({
                params: RequestQueryBuilder.create({
                  search: { id: participation?.bookingId },
                  join: [
                    { field: 'product' },
                    { field: 'product.translations' },
                    { field: 'product.location' },
                    { field: 'participations' },
                    { field: 'participations.member' },
                    { field: 'participations.organization' },
                    { field: 'linkedProduct' },
                    //{ field: 'linkedProduct.translations' },
                    { field: 'linkedProduct.location' }
                  ]
                })
              })
            )

            this.store.dispatch(
              saleItemActions.fetchMany({
                params: RequestQueryBuilder.create({
                  join: [
                    { field: 'sale' },
                    { field: 'product' },
                    { field: 'product.translations' },
                    { field: 'subscription' },
                    { field: 'subscription.translations' },
                    { field: 'course' },
                    { field: 'course.translations' },
                    { field: 'sale.organization' },
                    { field: 'sale.customer' },
                    { field: 'sale.saleItems' }
                  ],
                  search: { bookingId: participation?.bookingId }
                })
              })
            )
          })
        )
    }

    this.bsModalRef = this.modalService.show(ApproveBookingModalComponent, {
      initialState: {
        operation
      },
      class: 'modal-lg modal-dialog-scrollable'
    })
  }
  private mappedOperationFetch(operations) {
    combineLatest([
      this.store.select(productSelectors.selectEntities),
      this.store.select(organizationSelectors.selectEntities),
      this.store.select(participationSelectors.selectEntities),
      this.store.select(bookingSelectors.selectEntities)
    ])
      .pipe(
        take(1),
        map(
          ([
            productEntities,
            organizationEntities,
            participationEntities,
            bookingEntities
          ]) => {
            if (!operations) {
              return []
            }
            // this.updateOperations = true
            let operationsArray = []

            operations.map((operation) => {
              let orgDescription = null

              if (operation.organizationId) {
                orgDescription =
                  organizationEntities[operation.organizationId]?.description
              } else if (operation.participationId) {
                orgDescription =
                  organizationEntities[
                    participationEntities[operation.participationId]
                      ?.organizationId
                  ]?.description
              }

              let operationObj = {
                ...operation,
                organizationDescription: orgDescription,
                activityDescription:
                  productEntities[
                    bookingEntities[
                      participationEntities[operation.participationId]
                        ?.bookingId
                    ]?.linkedProductId
                  ]?.description ?? null,
                resourceDescription:
                  productEntities[
                    bookingEntities[
                      participationEntities[operation.participationId]
                        ?.bookingId
                    ]?.productId
                  ]?.description ?? null,
                startDate:
                  bookingEntities[
                    participationEntities[operation.participationId]?.bookingId
                  ]?.startDate ?? null
              }

              operationsArray.unshift(operationObj)
            })
            const sortedOperationArray: any[] = operationsArray.sort(
              (a, b) =>
                moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
            )

            this.operations$.next(sortedOperationArray)
          }
        )
      )
      .subscribe()
  }
  public onSearchTerm(searchTerm) {
    if (!!searchTerm && searchTerm.length > 0) {
      this.searchTerm$.next(searchTerm)
    } else {
      this.removeSearchTerm$.next(true)
      this.searchTerm = null
      this.page = 1
      setTimeout(() => {
        this.triggerCustomFetch('limit')
      }, 200)
    }
  }
  public selectNewPage(page) {
    this.page = page
    this.triggerCustomFetch('page')
  }
  public selectNewLimit(limit) {
    this.limit = limit
    this.page = 1
    this.searchTerm = null
    this.removeSearchTerm$.next(true)
    this.triggerCustomFetch('limit')
  }
  private triggerCustomFetch(selectionType) {
    if (selectionType == 'limit') {
      if (!this.endDate || !this.startDate) {
        this.dispatchFetchOperations()
      } else {
        this.dispatchFetchOperations()
      }
    } else {
      if (!this.endDate || !this.startDate) {
        if (this.searchTerm) {
          this.dispatchFetchOperationsWitTerm(this.searchTerm)
        } else {
          this.dispatchFetchOperations()
        }
      } else {
        if (this.searchTerm) {
          this.dispatchFetchOperationsWitTerm(this.searchTerm)
        } else {
          this.dispatchFetchOperations()
        }
      }
    }
  }
  private dispatchFetchOperations() {
    this.store.dispatch(
      documentActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: { byDefault: true }
        })
      })
    )

    this.store.dispatch(
      operationActions.fetchMany({
        params: RequestQueryBuilder.create({
          page: this.page,
          limit: this.limit,
          search: {
            $or: [
              //    { 'participation.booking.id': this.booking.id },
              { saleId: this.sale.id }
            ],

            forAdmins: true
          },
          join: [
            { field: 'organization' },
            // { field: 'sale' },
            { field: 'organization.parentOrganisation' },
            { field: 'member' },
            { field: 'sale' },
            { field: 'participation' },
            { field: 'participation.organization' },
            { field: 'participation.booking' },
            { field: 'participation.booking.product' },
            { field: 'participation.booking.linkedProduct' },
            { field: 'participation.booking.participations' },
            { field: 'userRole' },
            { field: 'userRole.role' }
          ],

          sort: { field: this.field, order: this.order as QuerySortOperator }
        })
      })
    )
  }

  private dispatchFetchOperationsWitTerm(termSearh: string) {
    this.store.dispatch(
      operationActions.fetchMany({
        params: RequestQueryBuilder.create({
          page: this.page,
          limit: this.limit,
          search: {
            // 'participation.booking.id': this.booking.id,
            saleId: this.sale.id,
            forAdmins: true,

            $or: [
              // {
              //   bookingId: {
              //     [CondOperator.CONTAINS_LOW]: termSearh
              //   }
              // },
              {
                'member.firstName': {
                  [CondOperator.CONTAINS_LOW]: termSearh
                }
              },
              {
                'member.email': {
                  [CondOperator.CONTAINS_LOW]: termSearh
                }
              },
              {
                'member.lastName': {
                  [CondOperator.CONTAINS_LOW]: termSearh
                }
              }
            ]
          },
          join: [
            { field: 'organization' },
            { field: 'organization.parentOrganisation' },
            { field: 'member' },
            { field: 'sale' },
            { field: 'participation' },
            { field: 'participation.organization' },
            { field: 'participation.booking' },
            { field: 'participation.booking.product' },
            { field: 'participation.booking.linkedProduct' },
            { field: 'participation.booking.participations' },
            { field: 'userRole' },
            { field: 'userRole.role' }
          ],

          sort: { field: this.field, order: this.order as QuerySortOperator }
        })
      })
    )
  }
}
